import { Chess, SQUARES, type Color, type PieceSymbol, type Square } from "chess.js";
import { applySanMove, type ChessRole } from "@chessla/shared";

// Teaching-facts architecture adapted from MIT-licensed Chess Vision Studio.
// Facts remain separate from prose so sentences and annotations share evidence.
export type TeachingFactKind =
  | "placement"
  | "capture"
  | "check"
  | "tempo"
  | "open_file"
  | "castle"
  | "development"
  | "fork"
  | "pin"
  | "support"
  | "hanging";

export interface TeachingFact {
  kind: TeachingFactKind;
  move: string;
  actor: ChessRole;
  target?: ChessRole;
  squares: string[];
}

export interface TeachingMarker {
  kind: "fork" | "pin" | "support" | "hanging" | "relative_pin" | "skewer" | "xray" | "discovered_potential" | "check";
  from: string;
  subject?: string;
  to: string;
  color: string;
  tint: string;
}

export const teachingMarkerHighlightSquares = (marker: TeachingMarker): string[] =>
  marker.subject ? [marker.subject] : [marker.from, marker.to];

export const POSITION_RELATIONSHIP_LEGEND = {
  pin: {
    color: "#8456cc",
    label: "King pin",
    detail: "A piece shields its king from a slider. Purple arrow ends at the king.",
  },
  relative_pin: {
    color: "#e879b9",
    label: "Relative pin",
    detail: "A piece shields a more valuable piece, not its king. Moving it may expose that piece.",
  },
  skewer: {
    color: "#f28c45",
    label: "Skewer alignment",
    detail: "A more valuable piece is attacked with a less valuable piece behind it. Not proof of a winning tactic.",
  },
  fork: {
    color: "#d6a84b",
    label: "Fork",
    detail: "One piece attacks at least two non-pawn targets. Check color takes priority on a checked king.",
  },
  hanging: {
    color: "#c95f5f",
    label: "Undefended target",
    detail: "An attacked piece has no geometric defenders. This does not prove a safe capture.",
  },
  support: {
    color: "#5f91c9",
    label: "Support",
    detail: "A friendly piece guards the selected piece. A pinned defender may not be able to move.",
  },
  xray: {
    color: "#45c5cf",
    label: "X-ray alignment",
    detail:
      "A slider faces an enemy target through one enemy blocker. Pins and skewers use their more specific colors.",
  },
  discovered_potential: {
    color: "#b5cf55",
    label: "Discovered-attack potential",
    detail: "A friendly blocker has a legal move that uncovers an attack. Potential, not a played or winning tactic.",
  },
  check: {
    color: "#ff405d",
    label: "Check",
    detail: "An attacker currently checks the side-to-move king. Each checking piece gets an arrow.",
  },
} satisfies Record<TeachingMarker["kind"], { color: string; label: string; detail: string }>;

export interface TeachingExplanationInput {
  fenBefore: string;
  playedMove: string;
  playedReply?: string;
  bestMove: string;
  continuation?: readonly string[];
  actualAfterCp?: number | null;
  bestFinalCp?: number | null;
  centipawnLoss?: number | null;
  opponent?: string;
}

export interface TeachingExplanation {
  explanation: string;
  rule: string;
  facts: TeachingFact[];
}

const ROLES: Record<PieceSymbol, ChessRole> = { p: "pawn", n: "knight", b: "bishop", r: "rook", q: "queen", k: "king" };
const roleName = (role: ChessRole): string => role;
const fileName = (square: string): string => square[0];
const isBackRank = (square: string, role: ChessRole): boolean =>
  role !== "pawn" && (square[1] === "1" || square[1] === "8");

const attacksFrom = (position: Chess, from: Square): Square[] => {
  const actor = position.get(from);
  return actor ? SQUARES.filter((target) => position.attackers(target, actor.color).includes(from)) : [];
};

const isOpenFile = (position: Chess, square: string): boolean =>
  !SQUARES.some((candidate) => candidate[0] === fileName(square) && position.get(candidate)?.type === "p");

const valuableTarget = (position: Chess, from: Square): { role: ChessRole; square: string } | null => {
  const actor = position.get(from);
  if (!actor) return null;
  for (const role of ["queen", "rook", "king", "bishop", "knight"] as const) {
    for (const square of attacksFrom(position, from)) {
      const piece = position.get(square);
      if (piece && piece.color !== actor.color && ROLES[piece.type] === role) return { role, square };
    }
  }
  return null;
};

const attackersOf = (position: Chess, target: Square, color: Color): Square[] => position.attackers(target, color);

const sliderRelationships = (position: Chess, from: Square): TeachingMarker[] => {
  const actor = position.get(from);
  if (!actor || !["b", "r", "q"].includes(actor.type)) return [];
  const diagonal = actor.type !== "r";
  const straight = actor.type !== "b";
  const directions = [
    ...(straight
      ? [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ]
      : []),
    ...(diagonal
      ? [
          [1, 1],
          [1, -1],
          [-1, 1],
          [-1, -1],
        ]
      : []),
  ];
  const file = from.charCodeAt(0) - 97;
  const rank = Number(from[1]) - 1;
  const markers: TeachingMarker[] = [];
  const values: Record<PieceSymbol, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: Infinity };
  for (const [df, dr] of directions) {
    let candidate: Square | null = null;
    for (let step = 1; step < 8; step += 1) {
      const nextFile = file + df * step;
      const nextRank = rank + dr * step;
      if (nextFile < 0 || nextFile > 7 || nextRank < 0 || nextRank > 7) break;
      const square = `${String.fromCharCode(97 + nextFile)}${nextRank + 1}` as Square;
      const piece = position.get(square);
      if (!piece) continue;
      if (!candidate) {
        candidate = square;
        continue;
      }
      const blocker = position.get(candidate)!;
      if (piece.color !== actor.color) {
        let kind: TeachingMarker["kind"] | null = null;
        if (blocker.color !== actor.color) {
          kind =
            piece.type === "k"
              ? "pin"
              : values[blocker.type] < values[piece.type]
                ? "relative_pin"
                : values[blocker.type] > values[piece.type]
                  ? "skewer"
                  : "xray";
        } else {
          // Test potential on the blocker's turn; en passant belongs only to the actual turn.
          const fields = position.fen().split(" ");
          if (fields[1] !== actor.color) {
            fields[1] = actor.color;
            fields[3] = "-";
          }
          const probe = new Chess(fields.join(" "));
          for (const move of probe.moves({ square: candidate, verbose: true })) {
            if (move.to === square) continue;
            probe.move(move);
            const uncovers =
              probe.get(square)?.color === piece.color && probe.attackers(square, actor.color).includes(from);
            probe.undo();
            if (uncovers) {
              kind = "discovered_potential";
              break;
            }
          }
        }
        if (kind) {
          const color = POSITION_RELATIONSHIP_LEGEND[kind].color;
          markers.push({
            kind,
            from,
            subject: candidate,
            to: square,
            color,
            tint: kind === "pin" ? "rgba(155,123,213,0.42)" : `${color}55`,
          });
        }
      }
      break;
    }
  }
  return markers;
};

const rayPins = (position: Chess, from: Square): string[][] =>
  sliderRelationships(position, from)
    .filter((marker) => marker.kind === "pin")
    .map((marker) => [marker.from, marker.subject!, marker.to]);

export const collectTeachingFacts = (fenBefore: string, line: readonly string[]): TeachingFact[] => {
  let position: Chess;
  try {
    position = new Chess(fenBefore);
  } catch {
    return [];
  }
  const facts: TeachingFact[] = [];
  for (const san of line) {
    try {
      const move = position.move(san);
      const actor = ROLES[move.piece];
      const from = move.from;
      const to = move.to;
      facts.push({ kind: "placement", move: san, actor, squares: [from, to] });
      if (move.captured)
        facts.push({ kind: "capture", move: san, actor, target: ROLES[move.captured], squares: [from, to] });
      if (move.isKingsideCastle() || move.isQueensideCastle())
        facts.push({ kind: "castle", move: san, actor: "king", squares: [from, to] });
      if (isBackRank(from, actor) && !isBackRank(to, actor))
        facts.push({ kind: "development", move: san, actor, squares: [from, to] });
      if (position.isCheck()) facts.push({ kind: "check", move: san, actor, target: "king", squares: [to] });

      const attackedEnemies = attacksFrom(position, to)
        .map((square) => ({ square, piece: position.get(square) }))
        .filter((item) => item.piece && item.piece.color !== move.color);
      const forkTargets = attackedEnemies.filter((item) => item.piece?.type !== "p");
      if (forkTargets.length >= 2)
        facts.push({ kind: "fork", move: san, actor, squares: [to, ...forkTargets.map((item) => item.square)] });

      const supported = attacksFrom(position, to)
        .map((square) => ({ square, piece: position.get(square) }))
        .find(
          (item) => item.piece?.color === move.color && !["p", "k"].includes(item.piece.type) && item.square !== to,
        );
      if (supported?.piece)
        facts.push({
          kind: "support",
          move: san,
          actor,
          target: ROLES[supported.piece.type],
          squares: [to, supported.square],
        });

      const hanging = attackedEnemies.find(
        (item) => item.piece?.type !== "k" && attackersOf(position, item.square, item.piece!.color).length === 0,
      );
      if (hanging?.piece)
        facts.push({
          kind: "hanging",
          move: san,
          actor,
          target: ROLES[hanging.piece.type],
          squares: [to, hanging.square],
        });
      for (const squares of rayPins(position, to)) facts.push({ kind: "pin", move: san, actor, squares });
      const target = valuableTarget(position, to);
      if (target && target.role !== "king")
        facts.push({ kind: "tempo", move: san, actor, target: target.role, squares: [to, target.square] });
      if ((actor === "rook" || actor === "queen") && isOpenFile(position, to))
        facts.push({ kind: "open_file", move: san, actor, squares: [to] });
    } catch {
      break;
    }
  }
  return facts;
};

export const positionRelationships = (fen: string): TeachingMarker[] => {
  const colors = {
    pin: { arrow: "#9b7bd5", tint: "rgba(155,123,213,0.42)" },
    support: { arrow: "#5f91c9", tint: "rgba(95,145,201,0.72)" },
    fork: { arrow: "#d6a84b", tint: "rgba(214,168,75,0.26)" },
    hanging: { arrow: "#c95f5f", tint: "rgba(201,95,95,0.26)" },
  } as const;
  const markers: TeachingMarker[] = [];
  let position: Chess;
  try {
    position = new Chess(fen);
  } catch {
    return markers;
  }
  for (const square of SQUARES) {
    const piece = position.get(square);
    if (!piece || !["b", "r", "q"].includes(piece.type)) continue;
    markers.push(...sliderRelationships(position, square));
  }
  const attacks = new Map<string, string[]>();
  const undefended = new Set<string>();
  for (const target of SQUARES) {
    const piece = position.get(target);
    if (!piece) continue;
    const defenders = position.attackers(target, piece.color);
    for (const from of defenders)
      markers.push({ kind: "support", from, to: target, color: colors.support.arrow, tint: colors.support.tint });
    if (piece.type !== "k" && defenders.length === 0) undefended.add(target);
    for (const from of position.attackers(target, piece.color === "w" ? "b" : "w")) {
      if (piece.type === "k" && piece.color === position.turn()) {
        const color = POSITION_RELATIONSHIP_LEGEND.check.color;
        markers.unshift({ kind: "check", from, to: target, color, tint: `${color}55` });
      }
      const targets = attacks.get(from) ?? [];
      targets.push(target);
      attacks.set(from, targets);
    }
  }
  for (const [from, targets] of attacks) {
    const forkTargets = targets.filter((target) => position.get(target as Square)?.type !== "p");
    if (forkTargets.length >= 2)
      for (const to of forkTargets)
        markers.push({ kind: "fork", from, to, color: colors.fork.arrow, tint: colors.fork.tint });
    for (const to of targets)
      if (
        undefended.has(to) &&
        !markers.some((marker) => marker.kind === "pin" && marker.from === from && marker.subject === to)
      )
        markers.push({ kind: "hanging", from, to, color: colors.hanging.arrow, tint: colors.hanging.tint });
  }
  return markers;
};

export const visionMarkers = (fen: string, focusSquare?: string): TeachingMarker[] =>
  positionRelationships(fen).filter((marker) =>
    marker.kind === "support"
      ? marker.to === focusSquare
      : !focusSquare || [marker.from, marker.subject, marker.to].includes(focusSquare),
  );

const moveFact = (facts: readonly TeachingFact[], move: string, kind: TeachingFactKind) =>
  facts.find((fact) => fact.move === move && fact.kind === kind);

const immediateSentence = (move: string, facts: readonly TeachingFact[]): string => {
  const fork = moveFact(facts, move, "fork");
  if (fork)
    return `${move} attacks two non-pawn targets at once. The continuation—not the shape alone—confirms whether that double attack holds.`;
  const pin = moveFact(facts, move, "pin");
  if (pin) return `${move} pins the piece on ${pin.squares[1]} to its king, taking away its freedom to move.`;
  const capture = moveFact(facts, move, "capture");
  if (capture?.target) return `${move} removes the ${roleName(capture.target)} on ${capture.squares[1]}.`;
  if (moveFact(facts, move, "check")) return `${move} takes the initiative by making the king answer.`;
  if (moveFact(facts, move, "castle")) return `${move} secures the king and brings the rook into the game.`;
  const development = moveFact(facts, move, "development");
  const hanging = moveFact(facts, move, "hanging");
  if (hanging?.target)
    return `${move} attacks the currently undefended ${roleName(hanging.target)} on ${hanging.squares[1]}; the reply shows whether it can escape or create counterplay.`;
  const support = moveFact(facts, move, "support");
  if (support?.target)
    return `${move} supports the ${roleName(support.target)} on ${support.squares[1]}, making the pieces work together.`;
  if (development)
    return `${move} develops the ${roleName(development.actor)} from ${development.squares[0]} to ${development.squares[1]}, adding another active piece instead of spending a move without improving the position.`;
  const placement = moveFact(facts, move, "placement");
  return placement
    ? `${move} moves the ${roleName(placement.actor)} from ${placement.squares[0]} to ${placement.squares[1]}. Its value comes from the reply and follow-up, not from the first move in isolation.`
    : `${move} begins the stronger continuation.`;
};

const payoffSentence = (move: string | undefined, facts: readonly TeachingFact[]): string => {
  if (!move) return "The important gain survives beyond the first move.";
  const capture = moveFact(facts, move, "capture");
  if (capture?.target)
    return `${move} removes the ${roleName(capture.target)} on ${capture.squares[1]}, so the gain survives the exchange.`;
  const tempo = moveFact(facts, move, "tempo");
  const openFile = moveFact(facts, move, "open_file");
  if (tempo?.target) {
    const file = openFile ? ` on the open ${fileName(openFile.squares[0])}-file` : "";
    return `${move} develops the ${roleName(tempo.actor)} with tempo on the ${roleName(tempo.target)}${file}.`;
  }
  if (openFile)
    return `${move} places the ${roleName(openFile.actor)} on the open ${fileName(openFile.squares[0])}-file.`;
  if (moveFact(facts, move, "check")) return `${move} keeps the initiative with another forcing move.`;
  const hanging = moveFact(facts, move, "hanging");
  if (hanging?.target)
    return `${move} keeps pressure on the undefended ${roleName(hanging.target)} on ${hanging.squares[1]}.`;
  const support = moveFact(facts, move, "support");
  if (support?.target)
    return `${move} also supports the ${roleName(support.target)} on ${support.squares[1]}, so the pieces work together.`;
  return `${move} completes the idea without giving the advantage back.`;
};

const evaluationSentence = (
  centipawnLoss: number | null | undefined,
  actual: number | null | undefined,
  best: number | null | undefined,
): string => {
  if (centipawnLoss != null) {
    if (centipawnLoss >= 9000) return "The better line avoids a decisive evaluation swing.";
    if (centipawnLoss >= 25)
      return `Compared with the game, it preserves about ${(centipawnLoss / 100).toFixed(1)} pawns of value.`;
    return "The line leaves the easier position to play.";
  }
  if (actual == null || best == null || best - actual < 25) return "The line leaves the easier position to play.";
  return `Compared with the game, the result is about ${((best - actual) / 100).toFixed(1)} pawns better.`;
};

export const buildTeachingExplanation = (input: TeachingExplanationInput): TeachingExplanation => {
  const first = applySanMove(input.fenBefore, input.bestMove);
  if (first?.san.endsWith("#")) {
    return {
      explanation: `${first.san} delivers checkmate. The game is over.`,
      rule: "A checkmate ends the game immediately.",
      facts: collectTeachingFacts(input.fenBefore, [first.san]),
    };
  }
  const continuation = input.continuation ?? [];
  const line = [input.bestMove, ...continuation].filter(Boolean);
  const facts = collectTeachingFacts(input.fenBefore, line);
  const playedFacts = input.playedReply
    ? collectTeachingFacts(input.fenBefore, [input.playedMove, input.playedReply])
    : [];
  const reply = continuation[0];
  const payoff = continuation[1];
  const replySentence = reply
    ? `The verified reply is ${reply}. Nikola checks your idea against it so the lesson remains grounded in objective analysis.`
    : "No legal reply was available from the verified engine line, so Nikola stops here instead of inventing one.";
  const openFile = payoff ? moveFact(facts, payoff, "open_file") : undefined;
  const rule = moveFact(facts, input.bestMove, "fork")
    ? "Before moving, scan for one piece that can attack two valuable targets."
    : moveFact(facts, input.bestMove, "pin")
      ? "When a piece shields its king, attack along that line before the pin disappears."
      : openFile
        ? "When a reply puts a valuable piece on an open file, look for a rook move with tempo."
        : "After finding the first move, check the opponent's most practical reply and identify what your next move improves.";

  return {
    explanation: [
      input.playedReply
        ? `In your game, ${immediateSentence(input.playedReply, playedFacts).replace(/\.$/, "").replace(" removes ", " removed ")}.`
        : "",
      immediateSentence(input.bestMove, facts),
      replySentence,
      payoffSentence(payoff, facts),
      evaluationSentence(input.centipawnLoss, input.actualAfterCp, input.bestFinalCp),
    ]
      .filter(Boolean)
      .join(" "),
    rule,
    facts,
  };
};
